import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false))
      .finally(() => setChecked(true));
  }, []);

  // Work-time tracking: while an Operations Manager has the app open, send a
  // heartbeat so the admin's "Team Activity" view can show live status & hours.
  useEffect(() => {
    if (user?.role !== "operations_manager") return;
    const beat = () => { api.post("/ops/heartbeat").catch(() => {}); };
    beat();
    const iv = setInterval(beat, 60000);
    const onVis = () => { if (document.visibilityState === "visible") beat(); };
    document.addEventListener("visibilitychange", onVis);
    const onUnload = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/ops/clock-out`;
        if (navigator.sendBeacon) navigator.sendBeacon(url);
      } catch { /* best effort */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [user?.role]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    return res.data;
  }, []);

  const register = useCallback(async (name, email, password, phone) => {
    const res = await api.post("/auth/register", { name, email, password, phone: phone || "" });
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    if (user?.role === "operations_manager") { try { await api.post("/ops/clock-out"); } catch { /* ignore */ } }
    await api.post("/auth/logout");
    setUser(false);
  }, [user?.role]);

  const refresh = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  const value = useMemo(() => ({ user, checked, login, register, logout, refresh }), [user, checked, login, register, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
